import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Dropdown,
    Empty,
    Select,
    Icon,
    Modal,
    Menu,
    message,
    Spin
} from "antd";
import Webcam from "react-webcam";
import { useInterval, useToggle, useUpdateEffect } from "react-use";
import styles from "./index.module.scss";
import { ModalProps } from "antd/es/modal";

import useRequest, { errorMessage } from "Utils/useRequest";
import UploadImage from "./components/UploadImage";
import { deletePhoneFile, getStudentPhoneFileList } from "API";
import FinishedImage from "./components/FinishedImage";
import Viewer from "react-viewer";
import { ResultMessage } from "components/ResultMessage";
import CountDown, { CountDownInstance } from "./components/CountDown";
import usePromiseCallback from "../../../../../../../Utils/usePromiseCallback";

interface ExamUploadModalProps extends ModalProps {
    examId: string;
    onSubmit?: (count: number) => void;
}

export default function ExamUploadModal({
    examId,
    onSubmit,
    ...props
}: ExamUploadModalProps) {
    const [active, setActive] = useState(false);
    const [error, setError] = useState<any>();

    const countRef = useRef<CountDownInstance>(null);

    const [deviceId, setDeviceId] = React.useState<string>("");
    const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
    const webcamRef = useRef<Webcam>(null);

    const [showViewer, setShowViewer] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);

    const [snapshot, setSnapshot] = useState<string[]>([]);

    const [photoList, , refresh] = useRequest(() =>
        getStudentPhoneFileList(examId)
    );

    useEffect(() => {
        if (!active) return;
        try {
            navigator.mediaDevices.enumerateDevices().then(mediaDevices => {
                const deviceList = mediaDevices.filter(
                    ({ kind }) => kind === "videoinput"
                );
                setDevices(deviceList);
                setDeviceId(deviceList[0]?.deviceId);
            });
        } catch (e) {
            Modal.error({ content: "您的设备无可用摄像头" });
        }
    }, [active]);

    useInterval(() => {
        setActive(webcamRef.current?.stream?.active || false);
    }, 1000);

    // 使用中状态变化
    useUpdateEffect(() => {
        setError(active ? undefined : "webcam error");
    }, [active]);

    const [loading, toggleLoading] = useToggle(false);
    // 开始拍照
    const onCapture = usePromiseCallback(async (time: number) => {
        try {
            if (time > 0) {
                toggleLoading();
                await countRef.current?.count(time);
                toggleLoading();
            }
            const base64 = await webcamRef.current?.getScreenshot();
            setSnapshot(snapshot => [...snapshot, base64!]);
        } catch (e) {
            message.error(errorMessage(e, "拍照出现错误"));
        }
    });

    async function removeSnapshot(base64: string) {
        await refresh();
        setSnapshot(snapshot => snapshot.filter(item => item !== base64));
    }

    //删除附件
    async function deletePics(fileUrl: string) {
        try {
            await deletePhoneFile(examId, fileUrl);
            refresh();
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    // 显示大图
    function onViewImage(index: number) {
        setViewerIndex(index);
        setShowViewer(true);
    }

    function onUserMedia(stream: MediaStream) {
        setError(undefined);
        setActive(true);
    }

    function onUserMediaError(error: any) {
        setError(error);
    }

    function handleMenuClick(e: any) {
        onCapture(e.key);
    }

    const menu = (
        <Menu onClick={handleMenuClick}>
            <Menu.Item key={0}>立即拍照</Menu.Item>
            <Menu.Item key={6}>延时6秒</Menu.Item>
            <Menu.Item key={10}>延时10秒</Menu.Item>
        </Menu>
    );

    const viewLazy = useMemo(() => {
        return (
            <Viewer
                visible={showViewer}
                activeIndex={viewerIndex}
                onClose={() => setShowViewer(false)}
                images={photoList?.fileList.map(src => ({ src }))}
                onChange={(_, index) => setViewerIndex(index)}
            />
        );
    }, [showViewer, viewerIndex, photoList?.fileList]);

    return (
        <Modal
            {...props}
            className={styles.root}
            title="上传作答内容"
            footer={false}>
            <div className={styles.webcam}>
                {!error && (
                    <Webcam
                        ref={webcamRef}
                        onUserMedia={onUserMedia}
                        onUserMediaError={onUserMediaError}
                        screenshotFormat="image/jpeg"
                    />
                )}

                {error && <div className={styles.error}>实时画面采集失败</div>}

                <CountDown ref={countRef} />
            </div>

            <div className={styles.main}>
                <div className={styles.header}>
                    {devices.length > 1 && (
                        <Select
                            placeholder="请选择摄像头"
                            value={deviceId}
                            onSelect={setDeviceId}
                            style={{ width: "100%", marginBottom: 10 }}>
                            {devices.map(item => (
                                <Select.Option
                                    key={item.deviceId}
                                    value={item.deviceId}>
                                    {item.label}
                                </Select.Option>
                            ))}
                        </Select>
                    )}

                    {devices.length === 0 && (
                        <div className={styles.tip}>无可用摄像头</div>
                    )}

                    <div className={styles.tip}>
                        提示：
                        <br />
                        请调整好桌子并确保作答内容清晰可见后，点击“拍照”，系统默认定时10秒拍照，如果拍照内容不清晰可删除后再继续拍照。
                    </div>
                </div>

                <div className={styles.photos}>
                    <div className={styles.list}>
                        {photoList?.fileList.map((item, index) => (
                            <FinishedImage
                                key={item + index}
                                url={item}
                                onClose={() => deletePics(item)}
                                onClick={() => onViewImage(index)}
                            />
                        ))}

                        {snapshot.map(item => (
                            <UploadImage
                                key={item}
                                examId={examId}
                                base64={item}
                                onFinished={() => removeSnapshot(item)}
                            />
                        ))}
                    </div>

                    {!photoList?.fileList.length && !snapshot.length && (
                        <Empty style={{ width: "100%", margin: 0 }} />
                    )}
                </div>

                <div className={styles.count}>
                    上传附件数：{photoList?.fileList.length || 0}
                </div>

                <Spin spinning={loading}>
                    <div className={styles.footer}>
                        <Button type="primary" ghost onClick={props.onCancel}>
                            继续作答
                        </Button>
                        <Dropdown overlay={menu}>
                            <Button type="primary">
                                拍照 <Icon type="down" />
                            </Button>
                        </Dropdown>
                        <Button
                            type="primary"
                            onClick={() => onSubmit?.(photoList?.fileNum || 0)}>
                            交卷
                        </Button>
                    </div>
                </Spin>
            </div>

            {viewLazy}
            {/*<Viewer*/}
            {/*    visible={showViewer}*/}
            {/*    activeIndex={viewerIndex}*/}
            {/*    onClose={() => setShowViewer(false)}*/}
            {/*    images={photoList?.fileList.map(src => ({ src }))}*/}
            {/*    onChange={(_, index) => setViewerIndex(index)}*/}
            {/*/>*/}
        </Modal>
    );
}
